import { SPIKE } from '../entities/families/spike';
import { WEDGE } from '../entities/families/wedge';
import { WALL } from '../entities/families/wall';
import { LADLE } from '../entities/families/ladle';
import { ORB } from '../entities/families/orb';
import { BAR } from '../entities/families/bar';
import { WING } from '../entities/families/wing';
import { RING } from '../entities/families/ring';
import { TINT } from '../entities/enemy-parts';
import { ATK, STATS } from './enemy-stats';
/* ============================================================
   敵人數值層 —— 把「造型表」變成「遊戲裡的敵人」
   ============================================================

   權威規格 `05-ENEMY.md`。這個檔只做三件事：**合成型別表**、**生成時刻表**、
   **變體曲線**。每一隻的數值與攻擊載荷在 `data/enemy-stats.ts`（含訂法說明），
   造型在 `entities/families/*.ts`（機械移植，不要手改）。

   ## 兩個換算係數（這是移植最容易出錯的地方）

   家族表的 `spd` 與 `rng` 是**圖鑑尺度**：檢視台裡一隻怪繞著展示台走的速度。
   直接搬進遊戲的話全部的敵人都追不上玩家（玩家 11.5，最快的 NADEL 只有 3.1）。
   體型（`h`/`w`）反而**不能換算** —— 規格 §1 的整張 px 對照表、家族剪影、
   Boss 尺寸全都是從那些數字長出來的，動了它整套辨識系統就失效。

   所以只換算「速度」與「距離」兩類，各用一個具名係數，錨點寫在下面。 */

/** 圖鑑步速 → 遊戲速度。錨點：NADEL 是新的主力雜兵（全表最常見），
    舊的主力雜兵「遊蕩者」是 7.2；3.1 × 2.3 = 7.13，落在同一格。 */
export var SPD_K = 2.3;
/** 圖鑑距離 → 遊戲距離。錨點：GEISSEL 的扇形噴吐要在「貼臉但不是接觸」的
    距離發生（4.2 × 2.6 ≈ 11 單位），對照舊的哨戒砲 34、突刺者 15。 */
export var RNG_K = 2.6;

/** 體積倍率（使用者 2026-08-03：「把目前的怪物整個體積放大一倍」）。

    **純視覺，碰撞半徑不受影響**（`r` 是獨立訂的，見 `enemy-stats.ts`）。
    這一項讓兩件事對上：規格的體型（NADEL 寬 0.62）本來只有碰撞直徑
    （1.90）的三分之一，看起來的大小與打起來的大小差太多。

    ⚠️ 這偏離了規格 §1 的量測基準（地面小怪 14–25px → 28–50px）。
    **八個家族全部套同一個倍率**（2026-08-03 移植其餘七族時照辦），
    所以家族之間的相對比例仍然是規格訂的那一套。 */
export var BODY_K = 2.0;

/** 八個家族。順序照規格 §2b 的 `FAMORDER`。 */
export var FAMILIES = [SPIKE, WEDGE, WALL, LADLE, ORB, BAR, WING, RING];

/** **所有造型**（40 隻，含深層 12 隻），照家族順序攤平。

    這跟 `ENEMY_TYPES` 不是同一件事：`ENEMY_TYPES` 是「進得了遊戲的」
    （要有 `enemy-stats.ts` 的數值才算），這張是「存在的」。
    圖鑑讀這一張 —— 圖鑑的職責是編目，不是回報生成表。

    深層 12 隻目前只有造型、沒有數值，所以它們在圖鑑裡看得到、
    在戰場上還遇不到（永遠停在「沒看過」）。那是誠實的狀態。 */
export var ALL_TYPES: any[] = [];
for (var _f = 0; _f < FAMILIES.length; _f++) {
  for (var _i = 0; _i < FAMILIES[_f].length; _i++) ALL_TYPES.push(FAMILIES[_f][_i]);
}

/** 家族表 + 數值 + 攻擊載荷，合成一張 `ETYPE` 相容的表。 */
export var ENEMY_TYPES: Record<string, any> = {};
for (var f = 0; f < FAMILIES.length; f++) {
  var fam = FAMILIES[f];
  for (var i = 0; i < fam.length; i++) {
    var D: any = fam[i], S = STATS[D.id];
    if (!S) continue;                     // 造型移植完但數值還沒訂 → 先不進遊戲
    ENEMY_TYPES[D.id] = {
      /* 造型與時間軸（1:1 移植，不要在這裡改）。 */
      id: D.id, fam: D.fam, tint: D.tint, code: D.code, zh: D.zh, role: D.role,
      /* **`h`/`w` 是「scale 1 時的尺寸」，不含 `BODY_K`。**
         `parts` 的座標也是 scale 1 的，而 Field 是用 `o.sc`（＝倍率）
         一次縮放整隻 —— 在這裡先乘一次的話，接地陰影與精英冠環會被
         **放大兩次**：冠環浮到頭頂上方兩單位、影子大到蓋住旁邊那隻。
         第一版就是這樣，實看截圖才發現（`shots/enemy-b1/e4-elite.png`）。
         要世界尺寸的地方自己乘 `bodyK`。 */
      h: D.h, w: D.w, bodyK: BODY_K, worldH: D.h * BODY_K,
      /* `fly` 是浮空高度（scale 1）。**精英冠環要掛在機身上方而不是頭頂**
         （規格的 `eliteKit`：浮空型的「頂」是 `fly + 0.62`，不是 `h`），
         沒有這一行的話 HORCHER 的冠環會浮在牠上方兩公尺的空氣裡。 */
      fly: D.fly || 0, crownH: D.fly ? D.fly + 0.62 : D.h + 0.30,
      parts: D.parts, ex: D.ex, pose: D.pose,
      q0: D.q0, vars: D.vars, gait: D.gait, turn: D.turn,
      tTel: D.tTel, tAtk: D.tAtk, tRec: D.tRec,
      /* 換算過的。原始值留一份（`spd0`/`rng0`）方便跟檢視台對帳。 */
      spd: D.spd * SPD_K, rng: D.rng * RNG_K, spd0: D.spd, rng0: D.rng,
      /* 戰鬥數值。 */
      hp: S.hp, dmg: S.dmg, imp: S.imp, xp: S.xp, score: S.score,
      r: S.r, y: S.y, cap: S.cap, atk: ATK[D.id], boom: ATK[D.id].boom,
      /* `accent` 是既有系統的通用欄位（生成環、精英標記、死亡特效都讀它）。
         新敵人的 accent 就是它的威脅色 —— 四色語彙因此自動延伸到特效上。 */
      accent: (TINT[D.tint] || TINT.red).hex,
      /* `body` 只被死亡碎片讀（碎片是獨立的粒子池，不吃敵人的材質）。 */
      body: 0x59616f,
      cn: D.code + ' ' + D.zh
    };
  }
}

/* ---------- 變體（規格 §5.3） ----------

   每一隻有三個變體：標準／兩個特化。變體只改比例與行為數值，
   **不新增 primitive、不改顏色** —— 加 primitive 等於該型別多一顆
   InstancedMesh，而且所有變體都要付；改顏色會讓四色語彙說謊。

     0:00–4:00   只有標準型
     4:00–10:00  特化型開始混入（各 20%）
     10:00–      三種等權 */
export function variantWeights(t) {
  if (t < 240) return [1, 0, 0];
  if (t < 600) return [0.60, 0.20, 0.20];
  return [0.34, 0.33, 0.33];
}

/* ---------- 生成時刻表 ----------

   原則沿用 VS：**每隔幾分鐘引入一種新的威脅方式**。密度只會讓畫面更吵，
   新的威脅方式才會讓玩家改變打法。

   ## 為什麼名冊會「換掉」而不是一直疊上去

   `InstancedMesh` 的 draw call 是按**型別數**算的（規格 §4）：一型約 6.3 顆，
   28 型全開 = 203 個 draw call。所以每一段的名冊都壓在 **8 型以內**
   （約 52 個 draw call），新的進來就要有舊的退場。

   退場不是損失 —— 「這一段的敵人組合換了」本身就是進展的訊號，
   而且同一隻在 15 分鐘後回來時玩家會記得它。

   ## 每一段都湊滿四個威脅色

   規格 §4.1。缺一色代表那一段少了一種「你要改什麼」的壓力，
   而玩家會把當下有效的打法一路帶到下一段 —— 那正是「乏味」的來源。

   ⚠️ **遠程不能提前。** 第一版把 LANZE 排在 0:30，壓力診斷直接掉到
   「1.3 分陣亡」（舊表的第一個遠程敵種在 7:00）。遠程的傷害不受
   「追不追得上玩家」限制，所以它在早期是純粹的加法。

   每一列必須加總為 1（`rollType` 是累加比對，不足 1 的話尾巴抽不到）。 */
export function enemyWeights(t) {
  /* 0:00 只有 NADEL —— 第一分半是教「走位」，不摻別的。 */
  if (t < 90) return [['nadel', 1]];
  /* 1:30 直射與突擊。罰「站在空地不動」與「站在衝刺線上」。 */
  if (t < 180) return [['nadel', 0.70], ['lanze', 0.18], ['keil', 0.12]];
  /* 3:00 滾輪（撞得更痛）＋ 盾衛（第一次出現「打不動的東西」）
     ＋ 一點點 GEISSEL。**四個威脅色在第 3 分鐘就到齊**：紫的地面圈是四種
     預警裡最好學的一種（亮完就炸，走開就好），先教它，後面的曲射、跳擊、
     空投才有得沿用。它的權重刻意很低 —— 這一段是認字，不是考試。 */
  if (t < 300) {
    return [['nadel', 0.52], ['lanze', 0.15], ['keil', 0.13], ['rolle', 0.10],
      ['geissel', 0.06], ['schild', 0.04]];
  }
  /* 5:00 範圍變成主力，加上曲射（MÖRSER）—— 罰「貼著怪堆輸出」與「站定不動」。 */
  if (t < 420) {
    return [['nadel', 0.40], ['lanze', 0.12], ['geissel', 0.13], ['rolle', 0.12],
      ['ramme', 0.11], ['morser', 0.07], ['schild', 0.05]];
  }
  /* 7:00 第一次有東西在你頭上（KRÄHE）、第一次有東西會炸（DORN）。 */
  if (t < 600) {
    return [['nadel', 0.32], ['geissel', 0.12], ['krahe', 0.11], ['dorn', 0.11],
      ['ramme', 0.11], ['spule', 0.09], ['pflug', 0.09], ['bake', 0.05]];
  }
  /* 10:00 近身橫掃（SENSE）＋ 覆蓋齊射（HARFE）＋ 支援（FÜHLER）。 */
  if (t < 780) {
    return [['nadel', 0.27], ['falke', 0.12], ['sense', 0.13], ['dorn', 0.11],
      ['harfe', 0.10], ['auge', 0.10], ['luke', 0.09], ['fuhler', 0.08]];
  }
  /* 13:00 **深層第一次出現**（規格 §2.2b）。牽引（HAKEN）與空投（SCHWARM）
     繼續「動你的位置」，同時混進三隻歪的、身上有洞的東西 ——
     一次三隻、三種顏色，讓玩家把「歪＝深層」這條規則跟已經學會的四色接起來。

     ⚠️ 深層一定要**混編**而不是整段換掉：整段換掉的話 NADEL 也會被抽走，
     而牠是密度的主體，場面會突然空掉。 */
  if (t < 960) {
    return [['nadel', 0.20], ['reifen', 0.12], ['haken', 0.10], ['schwarm', 0.10],
      ['blase', 0.08], ['kriecher', 0.14], ['laich', 0.13], ['faden', 0.13]];
  }
  /* 16:00 深層過半：旋渦與跳擊留著（它們是這一段的「已知威脅」），
     另外四格全部換成深層，四個威脅色一色一隻。 */
  if (t < 1080) {
    return [['nadel', 0.19], ['amboss', 0.11], ['wirbel', 0.11], ['horcher', 0.08],
      ['klumpen', 0.13], ['iris', 0.13], ['leier', 0.13], ['rippe', 0.12]];
  }
  /* 18:00 收尾：除了 NADEL 以外**整段都是深層**。
     三種紫色（SPROSS 跳擊圈／PFORTE 曲射圈／MOTTE 空投圈）同時在地上畫圈，
     最後兩分鐘仍然是「讀圖形」的考試 —— 只是這次每一隻看起來都不太對。 */
  return [['nadel', 0.17], ['kranz', 0.11], ['nest', 0.08], ['spross', 0.13],
    ['maul', 0.13], ['zunge', 0.13], ['pforte', 0.13], ['motte', 0.12]];
}

/** 這個時間點「已經登場」的敵種。波次劇本用它挑種 ——
    劇本不該指定一隻還沒被介紹過的敵人（支柱一：每一種會致死的東西
    都要先講一次，而波次是最不適合初次見面的場合）。 */
export function waveIds(t) {
  var w = enemyWeights(t) as Array<[string, number]>, out: string[] = [];
  for (var i = 0; i < w.length; i++) out.push(w[i][0]);
  return out;
}

/** 依威脅色挑一隻已登場的敵種；沒有就退回最常見的那隻。
    波次劇本用這個來表達「這一波要一堆直射的」而不用寫死 id。

    **同色有多隻時要輪替**（`light()` 與 `fast()` 都問紅色，永遠回同一隻的話
    每一波看起來都一樣）。輪替的指標用時間算而不是抽 RNG ——
    這個函式會被測試與波次排程在不同的時機呼叫，動到 RNG 的狀態
    會讓「同一顆種子」的意思變得很脆弱。 */
export function idByTint(t, tint) {
  var ids = waveIds(t), hit: string[] = [];
  for (var i = 0; i < ids.length; i++) if (ENEMY_TYPES[ids[i]].tint === tint) hit.push(ids[i]);
  if (!hit.length) return ids[0];
  return hit[Math.floor(Math.max(0, t) / 17) % hit.length];
}
